import { useCallback, useState } from 'react';

import type { RegistryInboundRow } from '@/types/registry-documents';

/**
 * The "Dismiss" dialog of the unmatched-mail list: which email it is open
 * for, and what its confirm does. Built like the attach dialog: one per
 * list, the email kept through the exit transition, a failure re-thrown
 * for the dialog to show.
 */
export function useRegistryInboundDismissDialog(
  dismiss: (rowId: string, note: string | null) => Promise<unknown>,
  onDismissed?: (row: RegistryInboundRow) => void
) {
  const [row, setRow] = useState<RegistryInboundRow | null>(null);
  const [opened, setOpened] = useState(false);

  const open = useCallback((target: RegistryInboundRow) => {
    setRow(target);
    setOpened(true);
  }, []);

  const close = useCallback(() => setOpened(false), []);

  const confirm = useCallback(
    async (note: string | null) => {
      if (!row) {
        return;
      }
      await dismiss(row.id, note);
      setOpened(false);
      onDismissed?.(row);
    },
    [row, dismiss, onDismissed]
  );

  return {
    /** Open the dialog for an email. */
    open,
    /** Spread onto `RegistryInboundDismissModal`. */
    modalProps: { opened, row, onClose: close, onConfirm: confirm },
  };
}
